import { useQuery, useQueries, useQueryClient } from "@tanstack/react-query";
import { api } from "jiffy-api";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { useCallback } from "react";

/**
 * Hook to fetch inventory data for a single product
 */
export function useProductInventoryData(productId?: string) {
  return useQuery({
    enabled: !!productId,
    queryKey: productId ? getProductInventoryQueryKey(productId) : [],
    queryFn: async () => {
      if (!productId) return undefined;
      return fetchProductInventory(productId);
    }
  });
}

/**
 * Hook to fetch inventory data for multiple products
 */
export function useMultipleProductInventoryData(productIds: string[]) {
  return useQueries({
    queries: productIds.map(productId => ({
      queryKey: getProductInventoryQueryKey(productId),
      queryFn: () => fetchProductInventory(productId),
      staleTime: 5 * 60 * 1000
    }))
  });
}

/**
 * Hook to warm cache for a single product inventory data
 */
export function useWarmProductInventoryCache() {
  const queryClient = useQueryClient();

  return useCallback(
    (productId: string) =>
      queryClient.prefetchQuery({
        queryKey: getProductInventoryQueryKey(productId),
        queryFn: () => fetchProductInventory(productId)
      }),
    [queryClient]
  );
}

const getProductInventoryQueryKey = (productId: string) => ["productInventory", productId];

const fetchProductInventory = async (productId: string) =>
  api.jiffyshirts.product.inventory({ slug: productId }).then(objNamesToCamelCase);
